import React from "react"
import { Link } from "gatsby"

import Layout from "../../components/promotions/layout"
import SEO from "../../components/common/seo"
import FaqSection from "../../components/home/FaqSection"
import PaymentLink from "../../components/promotions/paymentLink"
import { getSchemaForWebPageWithFaq, HIVE_SITE } from "../../utils/schema"
import { sponsorPageFaq, faqToSectionProps } from "../../utils/staticPageFaq"

// import { toQueryString } from "../../utils/urls"
import { sponsorPromotionLink } from "../../components/promotions/constants"

const SponsorPromotionPage = ({ data, path }) => {
  const faq = sponsorPageFaq()
  const pageSchema = getSchemaForWebPageWithFaq({
    pageUrl: `${HIVE_SITE}/partners/sponsor/`,
    name: "Sponsor Hive Index | Reach Community Builders and Seekers",
    description:
      "Put your brand in front of 25k+ monthly community seekers, owners, and professionals. Sponsorship includes sitewide banner placement, homepage shoutout, and exit screen ad.",
    faq,
    breadcrumbs: [
      { name: "Home", url: `${HIVE_SITE}/` },
      { name: "Partners", url: `${HIVE_SITE}/partners/` },
      { name: "Sponsor the site", url: `${HIVE_SITE}/partners/sponsor/` },
    ],
  })

  return (
    <Layout
      pageTitle="Sponsor the online community directory"
      enableShare={true}
      path={path}
      includeBlurb={true}
    >
      <SEO
        title="Sponsor Hive Index | Reach Community Builders and Seekers"
        description="Put your brand in front of 25k+ monthly community seekers, owners, and professionals. Sponsorship includes sitewide banner placement, homepage shoutout, and exit screen ad."
        generateImage={true}
        path={"/partners/sponsor/"}
        schema={pageSchema}
      />

      <div className="relative mt-6">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex items-center justify-between">
          <h2 className="pr-3 bg-white text-xl font-medium text-gray-900">
            Be the exclusive sponsor of The Hive Index for a month
          </h2>
        </div>
      </div>

      <p className="mt-2 text-gray-500">
        Put your brand in front of 25k+ monthly community seekers, owners, and
        professionals across The Hive Index.
        <br />
        Your text-based ad will appear at the very top of 100k+ monthly
        pageviews.
      </p>

      <div className="relative mt-6">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex items-center justify-between">
          <h2 className="pr-3 bg-white text-xl font-medium text-gray-900">
            Best fit for
          </h2>
        </div>
      </div>

      <div className="mt-4 text-gray-500">
        <ul className="list-disc pl-6 space-y-1">
          <li>Community platforms</li>
          <li>Community tools and analytics products</li>
          <li>Newsletter, event, or creator tools</li>
          <li>Agencies serving community-led businesses</li>
          <li>
            Brands trying to reach founders, marketers, creators, and niche
            community operators
          </li>
        </ul>
      </div>

      <div className="mx-auto mt-6 rounded-3xl ring-1 ring-gray-200 lg:mx-0 lg:flex lg:max-w-none">
        <div className="p-6 sm:p-8 lg:flex-auto">
          <h3 className="text-2xl font-bold tracking-tight text-gray-900">
            Site Sponsorship (1 month)
          </h3>
          <p className="mt-6 text-base leading-7 text-gray-600">
            Promote your brand to 25k+ monthly community seekers, owners, and
            professionals
          </p>
          <div className="mt-4 flex items-center gap-x-4">
            <h4 className="flex-none text-sm font-semibold leading-6 text-rose-600">
              Sponsorship includes
            </h4>
            <div className="h-px flex-auto bg-gray-100" />
          </div>
          <ul
            role="list"
            className="mt-4 grid grid-cols-1 gap-4 text-sm leading-6 text-gray-600 sm:grid-cols-2 sm:gap-6"
          >
            <li className="flex gap-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-6 w-5 flex-none text-rose-600"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 12.75l6 6 9-13.5"
                />
              </svg>
              "Brought to you by" link in the banner on every page (100k+
              pageviews/mo)
            </li>
            <li className="flex gap-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-6 w-5 flex-none text-rose-600"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 12.75l6 6 9-13.5"
                />
              </svg>
              Shoutout on homepage and site footer
            </li>
            <li className="flex gap-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-6 w-5 flex-none text-rose-600"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 12.75l6 6 9-13.5"
                />
              </svg>
              <div>Your ad on the community exit screen</div>
            </li>
          </ul>
        </div>

        <div className="-mt-2 p-2 lg:mt-0 lg:w-full lg:max-w-md lg:flex-shrink-0">
          <div className="rounded-2xl bg-yellow-50 py-12 text-center ring-1 ring-inset ring-yellow-100 lg:flex lg:flex-col lg:justify-center lg:py-12">
            <div className="mx-auto max-w-xs px-1">
              <p className="text-base font-semibold text-gray-600">
                1-month sitewide promotion
              </p>
              <p className="mt-6 flex items-baseline justify-center gap-x-2">
                <span className="text-5xl font-bold tracking-tight text-gray-900">
                  $1000
                </span>
              </p>
              <PaymentLink
                url={sponsorPromotionLink}
                params={{ utm_content: "sponsor_promotion" }}
                title="Sponsor the site"
                additionalClassName="mt-10"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="relative mt-6">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex items-center justify-between">
          <h2 className="pr-3 bg-white text-xl font-medium text-gray-900">
            Why it works
          </h2>
        </div>
      </div>

      <div className="mt-4 text-gray-500">
        <p>
          Hive Index visitors are not random display-ad traffic. They are
          browsing topics, comparing communities, and looking for places to
          join, promote, or learn from. That makes the sponsorship especially
          relevant for brands connected to community building, audience growth,
          creator tools, or niche professional networks.
        </p>
      </div>

      <FaqSection faqs={faqToSectionProps(faq)} />
    </Layout>
  )
}

export default SponsorPromotionPage
